import React from 'react';
import { Button } from '@/components/ui/button';

interface PaginationComponentProps {
  currentPage: number;
  totalPages: number;
  recordsPerPage: number;
  totalRecords: number;
  startIndex: number;
  endIndex: number;
  onPageChange: (page: number) => void;
  onRecordsPerPageChange: (recordsPerPage: number) => void;
  className?: string;
}

const PaginationComponent: React.FC<PaginationComponentProps> = ({
  currentPage,
  totalPages,
  recordsPerPage,
  totalRecords,
  startIndex,
  endIndex,
  onPageChange,
  onRecordsPerPageChange,
  className = ""
}) => {
  const handlePrevPage = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  };

  const handleRecordsPerPageChange = (newRecordsPerPage: number) => {
    onRecordsPerPageChange(newRecordsPerPage);
    // Do not trigger onPageChange here to avoid duplicate API calls; let parent handle resetting page
  };

  // Don't render if there are no records
  if (totalRecords === 0) {
    return null;
  }

   return (
    <div id="pagination_container" className={`flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-4 ${className}`} data-testid="pagination-component">
      <div className="flex items-center gap-3">
        <span className="text-sm font-medium text-foreground whitespace-nowrap">Rows per page:</span>
        <select
          id="pagination_records_per_page_select"
          value={recordsPerPage}
          onChange={(e) => handleRecordsPerPageChange(Number(e.target.value))}
          className="border border-border rounded-md px-3 py-1.5 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          data-testid="pagination-records-per-page-select"
        >
          <option value={10}>10</option>
          <option value={25}>25</option>
          <option value={50}>50</option>
        </select>
      </div>
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
        <div id="pagination_info" className="text-sm font-medium text-foreground whitespace-nowrap" data-testid="pagination-info">
          Showing {startIndex}-{endIndex} of {totalRecords}
        </div>
        <div className="flex items-center gap-2">
          <Button
            id="pagination_prev_btn"
            variant="outline"
            size="sm"
            onClick={handlePrevPage}
            disabled={currentPage === 1 || totalPages === 0}
            className="text-sm font-medium"
            data-testid="pagination-previous-button"
          >
            Previous
          </Button>
          <span id="pagination_page_info" className="text-sm font-semibold text-foreground whitespace-nowrap px-2" data-testid="pagination-page-info">
            Page {totalPages > 0 ? currentPage : 0} of {totalPages}
          </span>
          <Button
            id="pagination_next_btn"
            variant="outline"
            size="sm"
            onClick={handleNextPage}
            disabled={currentPage === totalPages || totalPages === 0}
            className="text-sm font-medium"
            data-testid="pagination-next-button"
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PaginationComponent;